/**
 * Mock identity & tenant data for Phase 1 prototype.
 * Replaces real SSO + tenant resolution in Phase 2.
 *
 * Personas are **tenant-specific**: each demo tenant has its own
 * org chart with industry-appropriate humans, role titles, departments
 * and email domains. The persona *id* (caio, cio, cro, ...) is stable
 * across tenants — RBAC and component code key off the id, never the
 * person.
 *
 * When a user switches tenants in the header, `AuthContext` re-resolves
 * the current user as the same-id persona in the new tenant. Result:
 * the role badge stays consistent, the human swaps to the right one.
 */
import { logger } from "@/lib/logger";

export const TENANT = {
  id: "acme-bank",
  name: "Acme Bank",
  industry: "Banking & Financial Services",
  region: "AU · Sydney",
  residency: "ap-southeast-2",
  tier: "ENTERPRISE",
  domain: "acmebank.com.au",
};

export const IDPS = [
  { id: "azure", name: "Microsoft Azure AD", icon: "M" },
  { id: "okta", name: "Okta", icon: "O" },
  { id: "google", name: "Google Workspace", icon: "G" },
];

// ────────────────────────────────────────────────────────────────────
// Persona registry (per tenant)
// ────────────────────────────────────────────────────────────────────
// Persona ids — stable across all tenants. Every tenant org chart MUST
// supply a persona for each of these ids so RBAC and dashboards work.
const PERSONA_IDS = [
  "caio", "cio", "cro", "ceo", "itsec", "legal", "dslead", "buhead", "auditor", "execviewer",
];

const ACME_BANK_PERSONAS = [
  { id: "caio",       name: "Jordan Patel",      role: "Chief AI Officer",                  roleCode: "CAIO",        initials: "JP", department: "Office of the CAIO",     email: "jordan.patel@acmebank.com.au",      color: "tralenex",    primary: "Strategy, approvals, ROI" },
  { id: "cio",        name: "Marcus Tan",        role: "Chief Information Officer",         roleCode: "CIO",         initials: "MT", department: "Technology",             email: "marcus.tan@acmebank.com.au",        color: "tralenex",    primary: "Integration, cost, vendors" },
  { id: "cro",        name: "Sasha Nair",        role: "Chief Risk Officer",                roleCode: "CRO",         initials: "SN", department: "Risk & Compliance",      email: "sasha.nair@acmebank.com.au",        color: "risk",        primary: "Risk, policy, compliance" },
  { id: "ceo",        name: "Eleanor Whitcombe", role: "CEO / Board Member",                roleCode: "CEO",         initials: "EW", department: "Office of the CEO",      email: "eleanor.whitcombe@acmebank.com.au", color: "gold",        primary: "Spend vs Value, board narrative" },
  { id: "itsec",      name: "Khalid Hassan",     role: "IT Security Officer",               roleCode: "IT Sec",      initials: "KH", department: "Information Security",   email: "khalid.hassan@acmebank.com.au",     color: "risk",        primary: "Stage 2 review, incidents" },
  { id: "legal",      name: "Priya Ravi",        role: "Legal & Compliance Officer",        roleCode: "Legal",       initials: "PR", department: "Legal & Compliance",     email: "priya.ravi@acmebank.com.au",        color: "opportunity", primary: "Stage 3 review, evidence packs" },
  { id: "dslead",     name: "Wei Lin Soo",       role: "AI / Data Science Lead",            roleCode: "DS Lead",     initials: "WS", department: "Data & AI",              email: "weilin.soo@acmebank.com.au",        color: "opportunity", primary: "Owned agents, drift, requests" },
  { id: "buhead",     name: "Aroha Wright",      role: "Department Head — Lending",         roleCode: "BU Head",     initials: "AW", department: "Lending",                email: "aroha.wright@acmebank.com.au",      color: "tralenex",    primary: "Submit opportunities & requests" },
  { id: "auditor",    name: "Hadley Brooks",     role: "Internal Audit Partner",            roleCode: "Auditor",     initials: "HB", department: "Internal Audit",         email: "hadley.brooks@acmebank.com.au",     color: "opportunity", primary: "Read-only access to policies, compliance, evidence and audit chain" },
  { id: "execviewer", name: "Olivia Strand",     role: "Executive Viewer / Board Observer", roleCode: "Exec Viewer", initials: "OS", department: "Office of the Board",    email: "olivia.strand@acmebank.com.au",     color: "gold",        primary: "Read-only board dashboards and summaries" },
];

const NORTHWIND_INSURANCE_PERSONAS = [
  { id: "caio",       name: "Vincent Kapoor",     role: "Chief AI & Analytics Officer",         roleCode: "CAIO",        initials: "VK", department: "Office of the CAIO",                email: "vincent.kapoor@northwindinsurance.com.au",     color: "tralenex",    primary: "Strategy, approvals, ROI" },
  { id: "cio",        name: "Helena Voss",        role: "Chief Information Officer",            roleCode: "CIO",         initials: "HV", department: "Technology & Platform Services",    email: "helena.voss@northwindinsurance.com.au",        color: "tralenex",    primary: "Integration, cost, vendors" },
  { id: "cro",        name: "Rohan Mehta",        role: "Chief Risk Officer",                   roleCode: "CRO",         initials: "RM", department: "Risk & Actuarial",                  email: "rohan.mehta@northwindinsurance.com.au",        color: "risk",        primary: "Risk, policy, compliance" },
  { id: "ceo",        name: "Madeleine Lefevre",  role: "Group CEO",                            roleCode: "CEO",         initials: "ML", department: "Office of the CEO",                 email: "madeleine.lefevre@northwindinsurance.com.au",  color: "gold",        primary: "Spend vs Value, board narrative" },
  { id: "itsec",      name: "Theo Nakamura",      role: "Chief Information Security Officer",   roleCode: "IT Sec",      initials: "TN", department: "Information Security",              email: "theo.nakamura@northwindinsurance.com.au",      color: "risk",        primary: "Stage 2 review, incidents" },
  { id: "legal",      name: "Bianca Romano",      role: "General Counsel & Compliance Lead",    roleCode: "Legal",       initials: "BR", department: "Legal & Compliance",                email: "bianca.romano@northwindinsurance.com.au",      color: "opportunity", primary: "Stage 3 review, evidence packs" },
  { id: "dslead",     name: "Anjali Desai",       role: "Head of Actuarial Modelling & AI",     roleCode: "DS Lead",     initials: "AD", department: "Actuarial & Pricing",               email: "anjali.desai@northwindinsurance.com.au",       color: "opportunity", primary: "Owned agents, drift, requests" },
  { id: "buhead",     name: "Tomás Ribeiro",      role: "Head of Claims (Motor & Home)",        roleCode: "BU Head",     initials: "TR", department: "Claims Operations",                 email: "tomas.ribeiro@northwindinsurance.com.au",      color: "tralenex",    primary: "Submit opportunities & requests" },
  { id: "auditor",    name: "Greta Fischer",      role: "External Audit Partner — Actuarial",   roleCode: "Auditor",     initials: "GF", department: "External Audit (Actuarial)",        email: "greta.fischer@northwindinsurance.com.au",      color: "opportunity", primary: "Read-only access to policies, compliance, evidence and audit chain" },
  { id: "execviewer", name: "Hideki Yamamoto",    role: "Non-Executive Director / Board Observer", roleCode: "Exec Viewer", initials: "HY", department: "Office of the Board",             email: "hideki.yamamoto@northwindinsurance.com.au",    color: "gold",        primary: "Read-only board dashboards and summaries" },
];

// ─── Sanity check (dev only): every tenant must supply a persona per id
if (process.env.NODE_ENV !== "production") {
  for (const [label, list] of [
    ["acme-bank", ACME_BANK_PERSONAS],
    ["northwind-insurance", NORTHWIND_INSURANCE_PERSONAS],
  ]) {
    const ids = new Set(list.map((p) => p.id));
    for (const required of PERSONA_IDS) {
      if (!ids.has(required)) {
        logger.warn(`[mockData] Tenant '${label}' is missing persona id '${required}'.`);
      }
    }
  }
}

export const PERSONAS_BY_TENANT = {
  "acme-bank": ACME_BANK_PERSONAS,
  "northwind-insurance": NORTHWIND_INSURANCE_PERSONAS,
};

/**
 * Resolve the persona list for a given tenant.
 * Falls back to Acme personas when the tenant id is unknown (e.g. Customer
 * Mode) so the persona switcher always renders 10 personas.
 */
export function getPersonasForTenant(tenantId) {
  return PERSONAS_BY_TENANT[tenantId] || ACME_BANK_PERSONAS;
}

/**
 * Backwards-compatible default export. Used by older imports that
 * pre-date the per-tenant refactor. Defaults to the Acme org chart.
 */
export const PERSONAS = ACME_BANK_PERSONAS;

export const SESSION = {
  idleTimeoutMinutes: 30,
  warningAtMinutes: 25,
  sessionLimitHours: 12,
};

export const COMPLIANCE_BADGES = [
  "SOC 2 II",
  "ISO 27001",
  "ISO 42001",
  "APRA CPS 234",
  "AU · Sydney",
];
